import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Eye, Loader2, Copy, Share2 } from "lucide-react";
import { toast } from "sonner";
import { getObsidianDeepDive } from "../lib/api";
import { ReportView } from "./Obsidian";

export default function ObsidianReport() {
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getObsidianDeepDive(reportId)
      .then((r) => setReport(r?.report))
      .catch((e) => setError(e?.response?.data?.detail || "Report not found"))
      .finally(() => setLoading(false));
  }, [reportId]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Permalink copied.");
    } catch {
      toast.error("Copy failed — select the URL manually.");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] text-zinc-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/obsidian"
          data-testid="report-back-to-obsidian"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-fuchsia-300 mb-6"
        >
          <ArrowLeft className="h-3 w-3" /> back to obsidian sink
        </Link>

        {loading && (
          <div className="text-fuchsia-300 text-[11px] uppercase tracking-widest text-center py-16">
            <Loader2 className="h-4 w-4 animate-spin inline-block mr-2" />
            Loading sealed report…
          </div>
        )}

        {error && (
          <div className="border border-rose-500/40 bg-rose-500/5 p-4 text-rose-300 font-mono text-sm">
            {error}
          </div>
        )}

        {report && (
          <>
            <div className="border-b border-zinc-900 pb-5 mb-6">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-fuchsia-300 mb-2">
                <Eye className="h-3 w-3" /> Obsidian deep-dive · sealed report
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl tracking-[0.05em]" data-testid="report-topic">
                {report.topic}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-4 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                <span>
                  by <span className="text-zinc-300">{report.user_handle ? "@" + report.user_handle : "anon"}</span>
                </span>
                <span>·</span>
                <span>{new Date(report.created_at).toISOString().slice(0, 19).replace("T", " ")}</span>
                <span>·</span>
                <span>{report.subclaim_count} sub-claims projected</span>
                <button
                  onClick={copyLink}
                  data-testid="report-copy-link"
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1 border border-zinc-800 hover:border-fuchsia-500 hover:text-fuchsia-300 transition-colors"
                >
                  <Share2 className="h-3 w-3" /> copy permalink
                </button>
              </div>
            </div>

            <ReportView report={report} />
          </>
        )}
      </div>
    </div>
  );
}
